import { BLACK, WHITE, hslToHex } from './symbolColors';
import type {
  LegendFittingStrategy,
  LegendStyleId,
} from './layoutTypes';

export type LegendStyleCategory = 'standard' | 'large' | 'medium';

export interface LegendStyleMeta {
  readonly id: LegendStyleId;
  readonly label: string;
  readonly category: LegendStyleCategory;
  readonly titleSizePt: number;
  readonly textSizePt: number;
  readonly titleAlignment: 'left' | 'center';
  readonly fittingStrategy: LegendFittingStrategy;
  readonly columnCount: number;
  readonly patchScale: number;
  readonly showLayerName: boolean;
  readonly showHeading: boolean;
  readonly background: {
    readonly enabled: boolean;
    readonly color: string;
  };
  readonly border: {
    readonly enabled: boolean;
    readonly color: string;
  };
}

const GRAY_PAPER = hslToHex(210, 8, 86);

export const LEGEND_STYLES: readonly LegendStyleMeta[] = [
  {
    id: 'centerTitle',
    label: 'Center Title Legend',
    category: 'standard',
    titleSizePt: 12,
    textSizePt: 8,
    titleAlignment: 'center',
    fittingStrategy: 'adjustFrame',
    columnCount: 1,
    patchScale: 1,
    showLayerName: true,
    showHeading: true,
    background: { enabled: true, color: WHITE },
    border: { enabled: false, color: BLACK },
  },
  {
    id: 'grayBackground',
    label: 'Gray Background Legend',
    category: 'standard',
    titleSizePt: 11,
    textSizePt: 8,
    titleAlignment: 'left',
    fittingStrategy: 'adjustFrame',
    columnCount: 1,
    patchScale: 1,
    showLayerName: true,
    showHeading: true,
    background: { enabled: true, color: GRAY_PAPER },
    border: { enabled: false, color: BLACK },
  },
  {
    id: 'blackBorder',
    label: 'Black Border Legend',
    category: 'standard',
    titleSizePt: 11,
    textSizePt: 8,
    titleAlignment: 'left',
    fittingStrategy: 'adjustFrame',
    columnCount: 1,
    patchScale: 1,
    showLayerName: true,
    showHeading: true,
    background: { enabled: true, color: WHITE },
    border: { enabled: true, color: BLACK },
  },
  {
    id: 'manualColumns',
    label: 'Manual Columns Legend',
    category: 'standard',
    titleSizePt: 11,
    textSizePt: 8,
    titleAlignment: 'left',
    fittingStrategy: 'manualColumns',
    columnCount: 2,
    patchScale: 1,
    showLayerName: true,
    showHeading: true,
    background: { enabled: false, color: WHITE },
    border: { enabled: false, color: BLACK },
  },
  {
    id: 'transparentBackground',
    label: 'Transparent Background Legend',
    category: 'standard',
    titleSizePt: 11,
    textSizePt: 8,
    titleAlignment: 'left',
    fittingStrategy: 'adjustFrame',
    columnCount: 1,
    patchScale: 1,
    showLayerName: true,
    showHeading: true,
    background: { enabled: false, color: WHITE },
    border: { enabled: false, color: BLACK },
  },
  {
    id: 'titleTextAligned',
    label: 'Title and Text Aligned Legend',
    category: 'standard',
    titleSizePt: 11,
    textSizePt: 8,
    titleAlignment: 'left',
    fittingStrategy: 'adjustFrame',
    columnCount: 1,
    patchScale: 1,
    showLayerName: false,
    showHeading: false,
    background: { enabled: true, color: WHITE },
    border: { enabled: false, color: BLACK },
  },
  {
    id: 'largeText',
    label: 'Large Text Legend',
    category: 'large',
    titleSizePt: 16,
    textSizePt: 13,
    titleAlignment: 'left',
    fittingStrategy: 'adjustColumnsAndFont',
    columnCount: 1,
    patchScale: 1.25,
    showLayerName: true,
    showHeading: true,
    background: { enabled: true, color: WHITE },
    border: { enabled: false, color: BLACK },
  },
  {
    id: 'titleLargeTextAligned',
    label: 'Title and Large Text Aligned Legend',
    category: 'large',
    titleSizePt: 16,
    textSizePt: 13,
    titleAlignment: 'left',
    fittingStrategy: 'adjustColumnsAndFont',
    columnCount: 1,
    patchScale: 1.25,
    showLayerName: false,
    showHeading: false,
    background: { enabled: false, color: WHITE },
    border: { enabled: false, color: BLACK },
  },
  {
    id: 'layerNamesLargeText',
    label: 'Layer Names and Large Text Legend',
    category: 'large',
    titleSizePt: 14,
    textSizePt: 13,
    titleAlignment: 'left',
    fittingStrategy: 'adjustColumns',
    columnCount: 1,
    patchScale: 1.25,
    showLayerName: true,
    showHeading: false,
    background: { enabled: true, color: WHITE },
    border: { enabled: false, color: BLACK },
  },
  {
    id: 'scaledPatchesMediumText',
    label: 'Scaled Patches and Medium Text Legend',
    category: 'medium',
    titleSizePt: 13,
    textSizePt: 10,
    titleAlignment: 'left',
    fittingStrategy: 'adjustColumnsAndFont',
    columnCount: 1,
    patchScale: 1.5,
    showLayerName: true,
    showHeading: true,
    background: { enabled: true, color: WHITE },
    border: { enabled: false, color: BLACK },
  },
  {
    id: 'headingsMediumText',
    label: 'Headings and Medium Text Legend',
    category: 'medium',
    titleSizePt: 13,
    textSizePt: 10,
    titleAlignment: 'left',
    fittingStrategy: 'adjustColumns',
    columnCount: 1,
    patchScale: 1,
    showLayerName: false,
    showHeading: true,
    background: { enabled: true, color: WHITE },
    border: { enabled: false, color: BLACK },
  },
  {
    id: 'titleMediumText',
    label: 'Title and Medium Text Legend',
    category: 'medium',
    titleSizePt: 13,
    textSizePt: 10,
    titleAlignment: 'left',
    fittingStrategy: 'adjustColumns',
    columnCount: 1,
    patchScale: 1,
    showLayerName: false,
    showHeading: false,
    background: { enabled: true, color: WHITE },
    border: { enabled: false, color: BLACK },
  },
] as const;

export function legendStyleMeta(styleId: LegendStyleId): LegendStyleMeta {
  return LEGEND_STYLES.find((style) => style.id === styleId) ?? LEGEND_STYLES[0];
}

export function legendStyleGroups(): readonly {
  readonly id: LegendStyleCategory;
  readonly label: string;
  readonly styles: readonly LegendStyleMeta[];
}[] {
  return [
    {
      id: 'standard',
      label: 'Legends',
      styles: LEGEND_STYLES.filter((style) => style.category === 'standard'),
    },
    {
      id: 'large',
      label: 'Large Text Legends',
      styles: LEGEND_STYLES.filter((style) => style.category === 'large'),
    },
    {
      id: 'medium',
      label: 'Medium Text Legends',
      styles: LEGEND_STYLES.filter((style) => style.category === 'medium'),
    },
  ];
}
